import React from 'react';
import { motion } from 'framer-motion';
import { InfoIcon } from 'lucide-react';
import { Horizon, horizons } from '../data/dashboardData';
type OccupancyForecastProps = {
  horizon: Horizon;
  onHorizonChange: (value: Horizon) => void;
};
function points(values: readonly number[]) {
  return values.map((value, index) => `${48 + index * 74},${164 - (value - 60) * 3.1}`).join(' ');
}
export function OccupancyForecast({
  horizon,
  onHorizonChange
}: OccupancyForecastProps) {
  const data = horizons[horizon];
  const peakIndex = data.occupancy.indexOf(Math.max(...data.occupancy));
  return <section aria-labelledby="occupancy-title" className="border border-[#d8d2c6] bg-[#fffcf7] p-4 sm:p-5" data-id="element-81">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-82">
        <div data-id="element-83">
          <div className="flex items-center gap-2" data-id="element-84">
            <h2 id="occupancy-title" className="font-serif text-xl text-[#0b2032]" data-id="element-85">
              Occupancy outlook
            </h2>
            <InfoIcon size={14} className="text-[#999185]" data-id="element-86" />
          </div>
          <p className="mt-1 text-xs text-[#73766f]" data-id="element-87">
            Portfolio rooms sold versus prior year
          </p>
        </div>
        <div className="flex border border-[#d6d0c5]" role="tablist" aria-label="Forecast horizon" data-id="element-88">
          {(Object.keys(horizons) as Horizon[]).map(item => <button role="tab" aria-selected={horizon === item} key={item} onClick={() => onHorizonChange(item)} className={`px-3 py-1.5 text-[10px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#aa8649] ${horizon === item ? 'bg-[#0b2032] text-white' : 'bg-white text-[#667078] hover:bg-[#f2eee6]'}`} data-id="element-89">
              {item}
            </button>)}
        </div>
      </div>
      <div className="mt-5 overflow-x-auto" data-id="element-90">
        <svg viewBox="0 0 540 210" className="h-[210px] min-w-[500px] w-full" role="img" aria-label={`${horizon} occupancy forecast chart, peaking at ${Math.max(...data.occupancy)} percent`} data-id="element-91">
          {[70, 80, 90, 100].map(tick => {
          const y = 164 - (tick - 60) * 3.1;
          return <g key={tick} data-id="element-92">
                <line x1="48" y1={y} x2="492" y2={y} stroke="#e2ddd3" strokeWidth="1" data-id="element-93" />
                <text x="8" y={y + 4} fill="#8a887f" fontSize="10" data-id="element-94">
                  {tick}%
                </text>
              </g>;
        })}
          <polyline points={points(data.lastYear)} fill="none" stroke="#aeb3b3" strokeWidth="1.5" strokeDasharray="4 5" data-id="element-95" />
          <motion.polyline key={horizon} initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} points={points(data.occupancy)} fill="none" stroke="#b08b4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-96" />
          {data.occupancy.map((value, index) => {
          const x = 48 + index * 74;
          const y = 164 - (value - 60) * 3.1;
          return <g key={`${value}-${index}`} data-id="element-97">
                <circle cx={x} cy={y} r={index === peakIndex ? 5 : 3} fill="#fffcf7" stroke="#b08b4e" strokeWidth="2" data-id="element-98" />
                {index === peakIndex ? <g data-id="element-99">
                    <rect x={x - 24} y={y - 31} width="48" height="21" rx="2" fill="#0b2032" data-id="element-100" />
                    <text x={x} y={y - 17} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="600" data-id="element-101">
                      {value}%
                    </text>
                  </g> : null}
                <text x={x} y="190" textAnchor="middle" fill="#77766f" fontSize="9" data-id="element-102">
                  {data.labels[index]}
                </text>
              </g>;
        })}
        </svg>
      </div>
      <div className="flex items-center gap-5 border-t border-[#e5e0d7] pt-3 text-[10px] text-[#74766f]" data-id="element-103">
        <span className="flex items-center gap-2" data-id="element-104">
          <i className="h-0.5 w-5 bg-[#b08b4e]" data-id="element-105" /> Current forecast
        </span>
        <span className="flex items-center gap-2" data-id="element-106">
          <i className="w-5 border-t border-dashed border-[#9fa6a6]" data-id="element-107" /> Last
          year
        </span>
        <span className="ml-auto hidden font-semibold text-[#34705c] sm:inline" data-id="element-108">
          +6.2 pts average uplift
        </span>
      </div>
    </section>;
}